import { atomWithDebounce } from '@rematter/ui-web';
import type { Atom } from 'jotai';
import { atom } from 'jotai';

import { createFacilityRuleMutationAtom } from '../_graphql/mutationFacilityRuleCreate';
import { deleteFacilityRuleMutationAtom } from '../_graphql/mutationFacilityRuleDelete';
import { updateFacilityRuleMutationAtom } from '../_graphql/mutationFacilityRuleUpdate';
import type { ComplianceFacilityRuleRecord } from '../_graphql/queryComplianceFacilityRules';
import { queryComplianceFacilityRulesAtom } from '../_graphql/queryComplianceFacilityRules';
import { queryComplianceRuleDefinitionsAtom } from '../_graphql/queryComplianceRuleDefinitions';
import type { LocationRecord } from '../_graphql/queryLocationList';

import { $rawLocationList } from './locations.state';
import { settingsRouteParamAtom } from './settingsModule.state';

import type { QueryAtomReadValue } from '~/shared/providers/api/createQueryAtomFactory';

export const $complianceRulesSearchTerm = atomWithDebounce('', 300);
export const $isFacilityRuleDialogOpen = atom(false);
export const $editingFacilityRule = atom<string | null>(null);

export const $complianceFacilityId = settingsRouteParamAtom(
  ['/compliance/rules/:facilityId'],
  'facilityId',
);

export const $facilityListForRules: Atom<QueryAtomReadValue<LocationRecord[]>> =
  atom((get) => {
    const rawList = get($rawLocationList);
    const searchTerms = get($complianceRulesSearchTerm.debouncedValue)
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    const data = (rawList.data ?? []).filter((facility) => {
      if (searchTerms.length === 0) return true;
      const searchable = [facility.name, facility.type]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchTerms.every((term) => searchable.includes(term));
    });

    return { ...rawList, data };
  });

export const $rawRuleDefinitions = queryComplianceRuleDefinitionsAtom(
  () => ({}),
);

export const $rawFacilityRules = queryComplianceFacilityRulesAtom(() => ({}));

export const $facilityRuleCounts = atom((get) => {
  const rules = get($rawFacilityRules);
  const counts = new Map<string, number>();
  for (const rule of rules.data ?? []) {
    if (rule.isEnabled) {
      counts.set(rule.facilityId, (counts.get(rule.facilityId) ?? 0) + 1);
    }
  }
  return counts;
});

export const $currentFacilityRules: Atom<
  QueryAtomReadValue<ComplianceFacilityRuleRecord[]>
> = atom((get) => {
  const facilityId = get($complianceFacilityId);
  const allRules = get($rawFacilityRules);

  if (!facilityId) return { ...allRules, data: [] };

  const data = (allRules.data ?? []).filter(
    (rule) => rule.facilityId === facilityId,
  );
  return { ...allRules, data };
});

export const $currentFacilityName = atom((get) => {
  const facilityId = get($complianceFacilityId);
  const facilities = get($rawLocationList);
  return facilities.data?.find((f) => f.id === facilityId)?.name;
});

export const $createFacilityRule = createFacilityRuleMutationAtom({
  onSuccess: (get) => {
    get($rawFacilityRules).refetch?.();
  },
});

export const $updateFacilityRule = updateFacilityRuleMutationAtom({
  onSuccess: (get) => {
    get($rawFacilityRules).refetch?.();
  },
});

export const $deleteFacilityRule = deleteFacilityRuleMutationAtom({
  onSuccess: (get) => {
    get($rawFacilityRules).refetch?.();
  },
});
